import React from "react";
import { Alert, Box, Button, Chip, FormControlLabel, Grid, Paper, Stack, Switch, Typography } from "@mui/material";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import { adminApi } from "../../api/admin";
import { getPanelPushSubscription, subscribePanelPush, webPushSupported } from "../../utils/webPushClient";

const OPTIONS = [
  ["commercial", "Consultas y contactos"], ["storefrontActivity", "Actividad en las webs"],
  ["orders", "Pedidos"], ["payments", "Cobros"], ["billing", "Suscripciones y vencimientos"],
  ["operations", "Proyectos, catálogo y reservas"], ["erp", "Gestión, cajas y stock"],
  ["security", "Cuentas y accesos"], ["panelActivity", "Navegación en el panel"], ["system", "Sistema"],
];
const DEFAULTS = Object.fromEntries(OPTIONS.map(([key]) => [key, true]));

// # SUPERADMIN: asociación por cuenta, canal y navegador. Nunca se cancela la
// suscripción del navegador: también puede servir al Owner y a los negocios.
export default function SuperAdminPushSettings({ onStatusChange }) {
  const [status, setStatus] = React.useState(null);
  const [subscription, setSubscription] = React.useState(null);
  const [preferences, setPreferences] = React.useState(DEFAULTS);
  const [working, setWorking] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [permission, setPermission] = React.useState(() => webPushSupported() ? Notification.permission : "unsupported");
  const requestRef = React.useRef(0);
  const supported = webPushSupported();
  const current = status?.ownSubscriptions?.find((row) => row.endpoint === subscription?.endpoint);
  const connected = permission === "granted" && current?.active === true;

  const load = React.useCallback(async () => {
    const version = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const response = await adminApi.pushStatus();
      if (version !== requestRef.current) return;
      setStatus(response);
      onStatusChange?.(response);
      const local = await getPanelPushSubscription();
      if (version !== requestRef.current) return;
      setSubscription(local);
      setPermission(webPushSupported() ? Notification.permission : "unsupported");
      const row = response.ownSubscriptions?.find((item) => item.endpoint === local?.endpoint);
      setPreferences({ ...DEFAULTS, ...row?.preferences });
    } catch (err) {
      if (version === requestRef.current) setError(err.message || "No se pudo consultar el estado de las notificaciones.");
    } finally {
      if (version === requestRef.current) setLoading(false);
    }
  }, [onStatusChange]);

  React.useEffect(() => {
    load();
    return () => { requestRef.current += 1; };
  }, [load]);

  const run = async (task) => {
    setWorking(true); setError(""); setMessage("");
    try { await task(); }
    catch (err) { setError(err.message || "No se pudo completar la operación."); }
    finally {
      setPermission(webPushSupported() ? Notification.permission : "unsupported");
      setWorking(false);
    }
  };
  const activate = () => run(async () => {
    const local = await subscribePanelPush(status?.publicKey);
    await adminApi.subscribePush(local.toJSON(), preferences);
    await load();
    setMessage("Alertas globales activadas para tu cuenta en este navegador. Podés enviar una prueba.");
  });
  const disconnect = () => run(async () => {
    if (!subscription?.endpoint) return;
    await adminApi.unsubscribePush(subscription.endpoint);
    await load();
    setMessage("Alertas globales desactivadas en este navegador.");
  });
  const test = () => run(async () => {
    const response = await adminApi.testPush({ endpoint: subscription.endpoint });
    if (!(response.result?.sent > 0)) throw new Error(response.message || "La prueba no pudo enviarse.");
    await load();
    setMessage(response.message);
  });
  const disabled = loading || working;

  return (
    <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 3 }, borderRadius: 4, border: "1px solid", borderColor: "divider" }}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ sm: "center" }}>
        <Box>
          <Typography variant="h5" fontWeight={950}>Alertas globales</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>Recibí novedades de toda WebProfit. Elegí qué escuchar en este navegador.</Typography>
        </Box>
        <Chip color={connected ? "success" : "default"} label={loading ? "Consultando…" : connected ? "Activas en este navegador" : "Sin conectar"} />
      </Stack>
      <Stack spacing={1.2} sx={{ mt: 2 }}>
        {error && <Alert severity="error">{error}</Alert>}
        {message && <Alert severity="success">{message}</Alert>}
        {status && !status.configured && <Alert severity="warning">El servidor necesita sus claves de notificaciones antes de conectar dispositivos.</Alert>}
        {!supported && <Alert severity="info">Este navegador no admite notificaciones web. En iPhone o iPad, probá desde WebProfit agregado a la pantalla de inicio.</Alert>}
        {permission === "denied" && <Alert severity="warning">Las notificaciones están bloqueadas. Permitilas en los permisos de este sitio y luego presioná Actualizar.</Alert>}
        {current?.active === false && <Alert severity="warning">Este dispositivo quedó desconectado. Presioná Reconectar notificaciones para reactivarlo.</Alert>}
      </Stack>
      <Grid container spacing={1} sx={{ mt: 1 }}>
        {OPTIONS.map(([key, label]) => (
          <Grid item xs={12} sm={6} key={key}>
            <FormControlLabel sx={{ m: 0, minHeight: 44 }} label={label} control={<Switch disabled={disabled} checked={preferences[key] !== false} onChange={(event) => setPreferences((prev) => ({ ...prev, [key]: event.target.checked }))} />} />
          </Grid>
        ))}
      </Grid>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
        <Button variant="contained" startIcon={<NotificationsActiveRoundedIcon />} disabled={disabled || !supported || permission === "denied" || !status?.configured} onClick={activate}>
          {working ? "Procesando…" : connected ? "Guardar preferencias" : "Reconectar notificaciones"}
        </Button>
        <Button variant="outlined" disabled={disabled || !connected || !status?.configured} onClick={test}>Enviar prueba</Button>
        <Button disabled={disabled || current?.active !== true} onClick={disconnect}>Desactivar aquí</Button>
        <Button startIcon={<RefreshRoundedIcon />} disabled={disabled} onClick={load}>Actualizar</Button>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
        Sólo superadministradores activos. El historial se conserva aunque desactives alertas. Las preferencias del Owner se administran por separado.
        {current?.lastSuccessAt ? ` Último envío aceptado: ${new Date(current.lastSuccessAt).toLocaleString("es-AR")}.` : ""}
      </Typography>
    </Paper>
  );
}
